import { IconTypes } from '@components/Icon';
import { AuthProvider } from '@context/Auth/AuthContext';
import i18next from 'i18next';
import { lazy } from 'react';
import { RouteObject } from 'react-router';
import { Navigate, Outlet } from 'react-router-dom';

const BackStage = lazy(() => import('@layouts/BackStage'));
const LoginLayout = lazy(() => import('@layouts/LoginLayout'));
const WebviewLayout = lazy(() => import('@layouts/WebView'));
const Admin = lazy(() => import('@pages/Admin'));
const Coupon = lazy(() => import('@pages/Coupon'));
const Error404 = lazy(() => import('@pages/Error404'));
const Error500 = lazy(() => import('@pages/Error500'));
const Login = lazy(() => import('@pages/Login'));
const LuckyDraw = lazy(() => import('@pages/LuckyDraw'));
const OTP = lazy(() => import('@pages/OTP'));
const Roulette = lazy(() => import('@pages/Roulette'));
const SetPassword = lazy(() => import('@pages/SetPassword'));
const TestPage = lazy(() => import('@pages/Test'));
const RouletteAnalytics = lazy(() => import('@pages/RouletteAnalytics'));

export interface CustomRouteConfig extends RouteObject {
  title: string;
  sidebarName?: string;
  children?: CustomRouteConfig[];
  excludeFromSidebar?: boolean;
  icon?: IconTypes;
}

export const PublicRoutes: CustomRouteConfig = {
  path: '',
  element: <WebviewLayout />,
  title: i18next.t('pageTitle.default'),
  caseSensitive: true,
  children: [
    {
      path: '',
      element: <Error404 />,
      title: i18next.t('pageTitle.404'),
      caseSensitive: true,
    },
    {
      path: 'lucky-draw/:token',
      element: <LuckyDraw />,
      title: i18next.t('pageTitle.luckyDraw'),
      caseSensitive: true,
    },
    {
      path: 'otp-verification/:token',
      element: <OTP />,
      title: i18next.t('pageTitle.otp'),
      caseSensitive: true,
    },
    {
      path: '500',
      element: <Error500 />,
      title: i18next.t('pageTitle.500'),
      caseSensitive: true,
    },
    {
      path: '*',
      element: <Error404 />,
      title: i18next.t('pageTitle.404'),
      caseSensitive: true,
    },
  ],
};

export const LoginRoutes: CustomRouteConfig = {
  path: '',
  element: <LoginLayout />,
  title: i18next.t('pageTitle.default'),
  caseSensitive: true,
  children: [
    {
      path: 'login',
      element: <Login />,
      title: i18next.t('pageTitle.login'),
      caseSensitive: true,
    },
    {
      path: 'set-password',
      element: <SetPassword />,
      title: i18next.t('pageTitle.setPassword'),
      caseSensitive: true,
    },
  ],
};

const AdminChildren: CustomRouteConfig[] = [
  {
    path: '',
    title: i18next.t('pageTitle.admin'),
    element: <Admin />,
    excludeFromSidebar: true,
    caseSensitive: true,
  },
  {
    path: 'marketing',
    title: i18next.t('pageTitle.marketingTools'),
    element: <Outlet />,
    icon: 'store',
    caseSensitive: true,
    children: [
      {
        path: '/',
        title: i18next.t('pageTitle.couponManagement'),
        element: <Navigate to="coupon" />,
        caseSensitive: true,
        excludeFromSidebar: true,
      },
      {
        path: 'coupon',
        title: i18next.t('pageTitle.marketingTools'),
        sidebarName: i18next.t('pageTitle.couponManagement'),
        element: <Coupon />,
        caseSensitive: true,
      },
      {
        path: 'roulette',
        title: i18next.t('pageTitle.marketingTools'),
        sidebarName: i18next.t('pageTitle.luckyDraw'),
        element: <Outlet />,
        caseSensitive: true,
        children: [
          {
            path: '/',
            title: i18next.t('pageTitle.marketingTools'),
            element: <Navigate to="all" />,
            caseSensitive: true,
            excludeFromSidebar: true,
          },
          {
            path: 'analytics/:eventId',
            title: i18next.t('pageTitle.marketingTools'),
            element: <RouletteAnalytics />,
            caseSensitive: true,
            excludeFromSidebar: true,
          },
          {
            path: ':filter',
            title: i18next.t('pageTitle.marketingTools'),
            element: <Roulette />,
            caseSensitive: true,
            excludeFromSidebar: true,
          },
        ],
      },
    ],
  },
  {
    path: 'admin-list',
    title: i18next.t('pageTitle.admin'),
    element: <Admin />,
    icon: 'plane',
    caseSensitive: true,
  },
  {
    path: 'layout',
    title: i18next.t('pageTitle.default'),
    element: <Outlet />,
    caseSensitive: true,
    children: [
      {
        path: '/',
        title: i18next.t('pageTitle.default'),
        element: <Navigate to="one" />,
        caseSensitive: true,
        excludeFromSidebar: true,
      },
      {
        path: ':tab',
        title: i18next.t('pageTitle.default'),
        element: <Outlet />,
        caseSensitive: true,
        excludeFromSidebar: true,
        children: [
          {
            path: '/',
            title: i18next.t('pageTitle.default'),
            element: <TestPage />,
            caseSensitive: true,
            excludeFromSidebar: true,
          },
          {
            path: ':modal',
            title: i18next.t('pageTitle.default'),
            element: <TestPage />,
            caseSensitive: true,
            excludeFromSidebar: true,
          },
        ],
      },
    ],
  },
  {
    path: '*',
    title: i18next.t('pageTitle.404'),
    element: <Error404 />,
    excludeFromSidebar: true,
    caseSensitive: true,
  },
];

export const AdminRoutes: CustomRouteConfig = {
  path: 'admin',
  element: (
    <AuthProvider user={{}}>
      <BackStage routes={AdminChildren} routeRoot={'admin'} />
    </AuthProvider>
  ),
  title: i18next.t('pageTitle.admin'),
  excludeFromSidebar: true,
  children: AdminChildren,
  caseSensitive: true,
};
